import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "../../../button";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { usePollContext } from "../../../../stores/poll";

import { parseCTAs } from "../parse-ctas";
import { handleCTAClick } from "..";

import styles from "../styles.modules.scss";
import { findRelatedMediaByTitle } from "../../../../utils/related-media";
import { useCustomModal } from "../../../../stores/customModal";
import { bridge } from "../../../../utils/bridge";
import {
    isEmptyGameParams,
    useGameParamsContext,
} from "../../../../stores/gameParams";
import { getAvatarId, getExpirationDate } from "../../../../utils/jwt";
import { Timer } from "../campaign-parts/timer";
import z from "zod";
import { Pressable } from "../../../pressable";
import { useSnowplowContext } from "../../../../analytics/snowplow";
import { Loading } from "../../../loading";
import { PollSlideType } from "../poll";
import { usePollResults, useVoteWithAnalytics } from "../../../poll";

export type DefaultSlideType = {
    type: "eventEntry" | null;
    id: string;
    background?: ImageSourceSets;
    ctas: InboxCTA[];
    displayTimer: boolean;
    isNew?: boolean;
    title: string;
    hideTitle: boolean;
    limit?: number;
    targeting?: Targeting;
    relatedMedia?: RelatedMedia[];
    lang: string;
};

const ConversationVariableSchema = z.object({
    name: z.string(),
    value: z.string(),
});

const TextSchema = z.object({
    type: z.literal("text"),
    content: z.string(),
});

const GuestTextSchema = z.object({
    type: z.literal("guestText"),
    content: z.string(),
    speaker: z.string(),
});

const RewardSchema = z.object({
    type: z.literal("reward"),
    name: z.string(),
    rewardId: z.string(),
});

const SidekickTextSchema = z.object({
    type: z.literal("sidekickText"),
    content: z.string(),
});

const LinkSchema = z.object({
    type: z.literal("link"),
    url: z.string().url(),
    label: z.string(),
});

const BodySchema = z.union([
    TextSchema,
    GuestTextSchema,
    SidekickTextSchema,
    RewardSchema,
    LinkSchema,
]);

type Message = {
    from: "character" | "you";
    body: z.infer<typeof BodySchema>[];
};

const VoucherStatusSchema = z.enum([
    "VOUCHER_ALREADY_REDEEMED",
    "VOUCHER_NOT_REDEEMED",
    "VOUCHER_EXPIRED",
    "VOUCHER_NOT_FOUND",
    "DONATION_NOT_FOUND",
]);
type VoucherStatus = z.infer<typeof VoucherStatusSchema>;

const VoucherSchema = z.object({
    token: z.string().optional(),
    status: VoucherStatusSchema,
});
type Voucher = z.infer<typeof VoucherSchema>;

const ConversationSchema = z.object({
    name: z.string(),
    conversation: z
        .object({
            start: z.object({
                sectionId: z.string(),
                title: z.string(),
            }),
            sections: z.array(
                z.object({
                    id: z.string(),
                    body: z.array(BodySchema),
                    questions: z.array(
                        z.object({
                            text: z.string(),
                            sectionId: z.string().optional(),
                        }),
                    ),
                    variables: z.array(
                        z.object({
                            name: z.string(),
                            value: z.string(),
                        }),
                    ),
                    condition: z
                        .object({
                            variable: z.string(),
                            value: z.string(),
                        })
                        .optional(),
                }),
            ),
            rewards: z
                .record(
                    z.string(),
                    z.object({
                        name: z.string(),
                        voucher: VoucherSchema,
                    }),
                )
                .optional(),
            variables: z.array(ConversationVariableSchema),
        })
        .nullable(),
    publishAt: z.string(),
    characters: z.array(z.string()),
});

const ConversationDataSchema = z.object({
    conversations: z.array(ConversationSchema),
});

type Reward = z.infer<typeof RewardSchema>;
type ConversationData = z.infer<typeof ConversationDataSchema>;
type ConversationVariable = z.infer<typeof ConversationVariableSchema>;

type ToggleVariablesFunction = (
    variables: { name: string; value: string }[],
    question: {
        sectionId: string;
        text: string;
    },
) => void;

function useFinalReward({
    pollId,
    pollSlide,
    voteCache,
    lang,
}: {
    pollId?: string;
    pollSlide?: PollSlideType;
    voteCache: {
        avatarId: number | null;
        votes: Record<string, string[] | null>;
    };
    lang: string;
}) {
    const { gameParams } = useGameParamsContext();
    const [finalRewardVoucher, setFinalRewardVoucher] =
        useState<Voucher | null>(null);
    const [error, setError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const getFinalReward = async () => {
        const jwt = !isEmptyGameParams(gameParams) ? gameParams.jwt : undefined;

        if (!jwt) {
            console.error("No JWT available");
            setError(true);
            setLoading(false);
            return;
        }

        const jwtExpiration = getExpirationDate(jwt);

        if (jwtExpiration && jwtExpiration < new Date()) {
            console.error("JWT is expired");
            await bridge().then((b) => b.refreshJwt());
            return;
        }

        const avatarId = getAvatarId(jwt ?? "") ?? "";

        const cache = localStorage.getItem(
            `golden-ripple-final-reward-${avatarId}-${lang}`,
        );

        // Use the cache if expiry date hasn't been reached yet and no publishAt dates have passed
        // This is to ensure we get new conversations that may have become active since last fetch
        if (cache) {
            try {
                const parsedCacheData = JSON.parse(cache);

                const validationResult = z
                    .object({
                        reward: VoucherSchema,
                        expiresAt: z.string(),
                    })
                    .safeParse(parsedCacheData);

                if (validationResult.success) {
                    const parsedCache = validationResult.data;

                    const expiresAt = new Date(parsedCache.expiresAt);
                    const now = new Date();

                    if (now < expiresAt) {
                        console.log("Cached final reward data hasn't expired");

                        setFinalRewardVoucher(parsedCache.reward);
                        setLoading(false);
                        setError(false);
                        return;
                    } else {
                        console.log("Cached final reward data is expired");
                    }
                } else {
                    console.error(
                        "Failed to validate cached final reward data:",
                        validationResult.error,
                    );
                }
            } catch (error) {
                console.error(
                    "Failed to parse cached final reward data",
                    error,
                );
            }
        }

        const res = await fetch(
            `${window.GOLDEN_RIPPLE_API_BASE}/get-final-reward`,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
            },
        );
        if (res.status !== 200) {
            setError(true);
            setLoading(false);
            throw new Error(`Failed to fetch final reward data: ${res.status}`);
        }
        const data = await res.json();

        const validationResult = VoucherSchema.safeParse(data);

        if (!validationResult.success) {
            setError(true);
            setLoading(false);
            console.error(
                "Failed to validate final reward data:",
                validationResult.error,
            );
            return;
        }

        const finalReward = validationResult.data;

        setError(false);
        setLoading(false);
        setFinalRewardVoucher(finalReward);

        const expiresAt = (() => {
            const now = new Date();
            // Now plus 1 hour
            return new Date(now.getTime() + 60 * 60 * 1000);
        })();

        localStorage.setItem(
            `golden-ripple-final-reward-${avatarId}-${lang}`,
            JSON.stringify({ expiresAt, reward: finalReward }),
        );
    };

    useEffect(() => {
        if (
            pollId !== undefined &&
            pollSlide !== undefined &&
            voteCache.votes[pollId] !== undefined &&
            voteCache.votes[pollId] !== null
        ) {
            const votedId = voteCache.votes[pollId][0];

            const voted = pollSlide.options.find(
                (option) => option.id === votedId,
            );
            if (!voted) return;

            getFinalReward();
        }
    }, [pollId, pollSlide, voteCache, gameParams]);

    return { finalRewardVoucher, error, loading, getFinalReward };
}

export const GoldenRippleSlide = ({
    slide,
    pollSlide,
}: {
    slide: DefaultSlideType;
    pollSlide?: PollSlideType;
}) => {
    const { ctas, hideTitle, title, limit = 60, relatedMedia, lang } = slide;

    const { conversations, error, loading } = useConversationData(lang);

    const [selectedCharacter, setSelectedCharacter] = React.useState<
        string | null
    >(null);
    const [variables, setVariables] = useState<ConversationVariable[]>([]);
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const { trackButtonClick } = useSnowplowContext();
    const pollId = pollSlide?.id;
    const options = pollSlide?.options;
    const {
        state: { voteCache, votingState },
        canVote,
        isClosed,
        actions: { refreshPollInfo, vote, openModal },
    } = usePollContext();
    const { optionsWithResults, showResults } = usePollResults(
        pollId || "",
        options || [],
    );
    React.useEffect(() => {
        refreshPollInfo(pollId || "");
    }, [pollId]);
    const voteInPoll = (optionId: string[]) => vote(pollId || "", optionId);
    const voteWithAnalytics = useVoteWithAnalytics(
        pollId || "",
        options || [],
        voteInPoll,
    );
    const [voteConfirmation, setVoteConfirmation] = useState<string | null>(
        null,
    );
    const [votingInProgress, setVotingInProgress] = useState<boolean>(false);
    const { finalRewardVoucher, getFinalReward } = useFinalReward({
        pollId,
        pollSlide,
        voteCache,
        lang,
    });

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrolledBeyondOriginal, setScrolledBeyondOriginal] =
        useState<boolean>(false);

    const relatedMediaUrls = useMemo(
        () =>
            relatedMedia
                ?.map((media) => media.image?.url)
                .filter((image) => image !== undefined) || [],
        [relatedMedia],
    );

    const relatedMediaLoaded = useImageLoader(relatedMediaUrls);
    const loaded = relatedMediaLoaded && !loading;

    useEffect(() => {
        if (!conversations) return;
        const allVariables = conversations.reduce((acc, curr) => {
            return [...acc, ...(curr.conversation?.variables || [])];
        }, [] as ConversationVariable[]);

        setVariables(allVariables);
    }, [conversations]);

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollRef.current) return;
            const scrollLeft = scrollRef.current.scrollLeft;

            if (scrollLeft > 100) {
                setScrolledBeyondOriginal(true);
            }
        };

        scrollRef.current?.addEventListener("scroll", handleScroll);

        return () => {
            scrollRef.current?.removeEventListener("scroll", handleScroll);
        };
    }, [loaded]);

    const limitedTitle = title.substring(0, limit);

    const parsedCTAs = parseCTAs(ctas, handleClick);

    const relatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            wall: "--wall",
            watch: "--watch",
            "reward-icon": "--reward-icon",
            "info-icon": "--info-icon",
            magnifier: "--magnifier",
            hand: "--hand",
            check: "--check",
            "modal-sidekick": "--modal-sidekick",
            mandy: "--character-mandy",
            chester: "--character-chester",
            frank: "--character-frank",
            leon: "--character-leon",
            fang: "--character-fang",
            edgar: "--character-edgar",
            colette: "--character-colette",
            bonnie: "--character-bonnie",
            doug: "--character-doug",
            glowbert: "--character-glowbert",
            pierce: "--character-pierce",
            mrp: "--character-mrp",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        return styles;
    }, [relatedMedia]);

    const modalStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            watch: "--watch",
            "modal-background": "--modal-background",
            "modal-prop": "--modal-prop",
            "modal-split": "--modal-split",
            "modal-sidekick": "--modal-sidekick",
            "modal-shadow": "--modal-shadow",
            "icon-mandy": "--icon-mandy",
            "icon-chester": "--icon-chester",
            "icon-frank": "--icon-frank",
            "icon-spike": "--icon-spike",
            "icon-leon": "--icon-leon",
            "icon-fang": "--icon-fang",
            "icon-edgar": "--icon-edgar",
            "icon-colette": "--icon-colette",
            "icon-bonnie": "--icon-bonnie",
            "icon-doug": "--icon-doug",
            "icon-sidekick": "--icon-sidekick",
            "icon-buster": "--icon-buster",
            "icon-buzz": "--icon-buzz",
            "icon-bruce": "--icon-bruce",
            "icon-mrp": "--icon-mrp",
            "icon-glowbert": "--icon-glowbert",
            "reward-chaosdrop": "--reward-chaosdrop",
            "reward-starrdrop": "--reward-starrdrop",
            "reward-bling": "--reward-bling",
            "reward-coin": "--reward-coin",
            "reward-powerpoint": "--reward-powerpoint",
            mandy: "--character-mandy",
            chester: "--character-chester",
            frank: "--character-frank",
            leon: "--character-leon",
            fang: "--character-fang",
            edgar: "--character-edgar",
            colette: "--character-colette",
            bonnie: "--character-bonnie",
            doug: "--character-doug",
            glowbert: "--character-glowbert",
            pierce: "--character-pierce",
            mrp: "--character-mrp",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        return styles;
    }, [relatedMedia]);

    const pollTimerString =
        pollSlide !== undefined && pollSlide.relatedMedia !== undefined
            ? pollSlide.relatedMedia.find((media) => media.title === "timer")
                  ?.label
            : undefined;

    const timerString =
        pollTimerString !== undefined
            ? pollTimerString
            : relatedMedia?.find((media) => media.title === "timer")?.label;

    const toggleCharacter = (sectionId: string) => {
        setSelectedCharacter((prev) => (prev === sectionId ? null : sectionId));
    };

    const toggleVariables = (
        variables: {
            name: string;
            value: string;
        }[],
        question: {
            sectionId: string;
            text: string;
        },
    ) => {
        setVariables((prevVariables) => {
            return prevVariables.map((variable) => {
                const updatedVariable = variables.find(
                    (v) => v.name === variable.name,
                );

                if (!updatedVariable) return variable;

                /*
                button_id: goldenRippleUpdateVariable
                additional_information:
                    variableName: "Fang"
                    variableValue: "1"
                    variablePreviousValue: "0"
                    toggledFromSectionId: "a4064184-d0e0-4b64-8032-e4efa6a27bb5"
                    toggledFromLabel: "900,000"
                */
                trackButtonClick("goldenRippleUpdateVariable", undefined, {
                    variableName: updatedVariable.name,
                    variableValue: updatedVariable.value,
                    variablePreviousValue: variable.value,
                    toggledFromSectionId: question.sectionId,
                    toggledFromLabel: question.text,
                });

                return { ...variable, value: updatedVariable.value };
            });
        });
    };

    const votingIsActive =
        pollId !== undefined && canVote(pollId) && !isClosed(pollId);

    const votedForCharacter = (() => {
        if (
            pollId !== undefined &&
            pollSlide !== undefined &&
            voteCache.votes[pollId] !== undefined &&
            voteCache.votes[pollId] !== null
        ) {
            const votedId = voteCache.votes[pollId][0];

            const voted = pollSlide.options.find(
                (option) => option.id === votedId,
            );
            if (!voted) return null;
            return voted.title;
        } else {
            return null;
        }
    })();

    const toggleVoteConfirmation = (character: string, value: boolean) => {
        setVoteConfirmation(value ? character : null);
    };

    const handleVote = async (character: string) => {
        if (!options || pollId === undefined) return;

        const option = options.find((option) => option.title === character);

        if (option) {
            try {
                setVotingInProgress(true);
                await voteWithAnalytics([option.id]);
                refreshPollInfo(pollId);

                if (votingState === "fail") {
                    setTimeout(() => {
                        refreshPollInfo(pollId);
                        setVotingInProgress(false);
                    }, 1000);
                    return;
                }
                setTimeout(() => {
                    refreshPollInfo(pollId);
                    setVotingInProgress(false);
                }, 1000);
                getFinalReward();
            } catch (e) {
                setTimeout(() => {
                    refreshPollInfo(pollId);
                    setVotingInProgress(false);
                }, 1000);
                console.error("Error voting for door", e);
            }
        }
    };

    const claimFinalReward = (voucher: Voucher) => {
        bridge().then(async (b) => {
            if (voucher.token === undefined) {
                return;
            }

            // Delete all localStorage items that start with golden-ripple-conversations- to
            // force refetching conversations (and the reward status) next time
            const localStorageKeys = Object.keys(localStorage);

            localStorageKeys.forEach((key) => {
                if (key.startsWith("golden-ripple-final-reward-")) {
                    localStorage.removeItem(key);
                }
            });

            if (voucher.status === "VOUCHER_NOT_REDEEMED") {
                /*
                    button_id: goldenRippleClaimReward
                    additional_information:
                        rewardId: "UUID of the reward, *not* Spell Factory ID"
                        rewardName: "1 Chaos Drop"
                    */
                trackButtonClick("goldenRippleClaimReward", undefined, {
                    rewardId: "final-reward",
                    rewardName: "Pierce Box",
                });
            }

            localStorage.setItem("golden-ripple-final-reward-claimed", "true");

            b.claimVoucherQuiet(voucher.token);
            b.close();
        });
    };

    const hasClaimedFinalReward =
        localStorage.getItem("golden-ripple-final-reward-claimed") === "true";

    return (
        <div
            className={`slide ${styles.slide} ${lang}`}
            style={{ ...relatedMediaStyles }}
        >
            {finalRewardVoucher !== null && (
                <div className="final-reward-button">
                    <Button
                        onClick={() => {
                            if (finalRewardVoucher) {
                                claimFinalReward(finalRewardVoucher);
                            } else {
                                console.error("Final reward not found");
                            }
                        }}
                        variant="twitch"
                        size="sm"
                    >
                        <span className="buttonTextWrapper">
                            {hasClaimedFinalReward ? (
                                <FormattedMessage id="globalText.langGoldenRippleClaimed" />
                            ) : (
                                <FormattedMessage id="globalText.langGoldenRippleClaim" />
                            )}
                        </span>
                    </Button>
                </div>
            )}
            {finalRewardVoucher !== null && !hasClaimedFinalReward && (
                <div className="final-reward-sidekick">
                    <div className="speech-bubble">
                        <span>
                            <FormattedMessage id="globalText.langGoldenRippleThanks" />
                        </span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 19.88 25.41"
                        >
                            <g id="Layer_1-2" data-name="Layer 1">
                                <path
                                    style={{
                                        opacity: 0.25,
                                    }}
                                    d="M0,25.41l17.39-9.89c1.54-.87,2.49-2.51,2.49-4.28v-2.55L0,22.85v2.55Z"
                                />
                                <path d="M0,22.85l17.39-9.89c1.54-.87,2.49-2.51,2.49-4.28,0-2.27-1.56-4.25-3.77-4.79L0,0v22.85Z" />
                                <path
                                    style={{
                                        fill: "#fff",
                                    }}
                                    d="M0,19.42l16.38-8.7c1.83-.97,1.48-3.69-.53-4.18L0,2.75v16.67Z"
                                />
                            </g>
                        </svg>
                    </div>
                    <Button
                        onClick={() => {
                            if (finalRewardVoucher) {
                                claimFinalReward(finalRewardVoucher);
                            } else {
                                console.error("Final reward not found");
                            }
                        }}
                        variant="twitch"
                    >
                        <span className="buttonTextWrapper">
                            <FormattedMessage id="globalText.langGoldenRippleClaim" />
                        </span>
                    </Button>
                    <div className="character"></div>
                    <div className="sidekick-shadow"></div>
                </div>
            )}
            <div className="carousel-scroll-point"></div>
            <div className={`loader ${loaded ? "loaded" : ""}`}>
                <Loading />
            </div>
            {loaded && !error && (
                <GesturingHand
                    shouldHide={scrolledBeyondOriginal}
                    lower={finalRewardVoucher !== null}
                />
            )}
            {votingState === "fail" && (
                <div className="vote-error">
                    <FormattedMessage id="globalText.langVoteTryAgainLabel" />
                </div>
            )}
            {timerString !== undefined && (
                <Timer timerString={timerString} lang={String(slide.lang)} />
            )}
            <Info locked={finalRewardVoucher === null} />
            <div className={`content ${styles.content}`}>
                {limitedTitle && !hideTitle && (
                    <>
                        <div className={`filter ${styles.filter}`} />

                        <div
                            className={`title ${styles.title}`}
                            data-title={limitedTitle}
                        >
                            {pollSlide !== undefined
                                ? pollSlide.title
                                : limitedTitle}
                        </div>
                    </>
                )}

                <div className={`wrapper ${styles.wrapper}`}>
                    {parsedCTAs.length ? (
                        <div
                            className={`ctas ${
                                parsedCTAs.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {parsedCTAs.map(
                                ({ label, onClick, variant }, index) => (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                        size="sm"
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                ),
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
            {!error ? (
                <div
                    className="characters-wrapper swiper-no-swiping"
                    ref={scrollRef}
                >
                    <div className="characters">
                        {conversations?.map((set, index) => {
                            if (set.conversation === null) {
                                return (
                                    <Fragment key={index}>
                                        {set.characters.map(
                                            (character, cIndex) => (
                                                <Character
                                                    key={`${index}-character-${cIndex}`}
                                                    selected={false}
                                                    handleClick={() => {}}
                                                    modalStyles={modalStyles}
                                                    name={character}
                                                    sectionId=""
                                                    toggleVariables={() => {}}
                                                    variables={[]}
                                                    timer={set.publishAt}
                                                    lang={lang}
                                                    isNew={false}
                                                    handleLinkClick={
                                                        handleClick
                                                    }
                                                    trackButtonClick={
                                                        trackButtonClick
                                                    }
                                                    votingIsActive={false}
                                                    votingInProgress={false}
                                                    voteConfirmation={false}
                                                    toggleVoteConfirmation={() => {}}
                                                    vote={() => {}}
                                                    votedFor={false}
                                                    hasVoted={false}
                                                />
                                            ),
                                        )}
                                    </Fragment>
                                );
                            }

                            const characters = set.conversation.sections.find(
                                (section) =>
                                    section.id ===
                                    set.conversation?.start.sectionId,
                            )?.questions;

                            return set.characters.map(
                                (characterName, cIndex) => {
                                    const sectionId = characters?.find(
                                        (q) => q.text === characterName,
                                    )?.sectionId;

                                    const isNew =
                                        localStorage.getItem(
                                            `golden-ripple-new-label-seen-${sectionId}`,
                                        ) !== "true";

                                    if (sectionId === undefined) {
                                        return null;
                                    }

                                    return (
                                        <Character
                                            key={`${index}-character-${cIndex}`}
                                            selected={(() => {
                                                if (
                                                    finalRewardVoucher !== null
                                                ) {
                                                    return (
                                                        selectedCharacter ===
                                                        sectionId
                                                    );
                                                }

                                                if (votingIsActive) {
                                                    return true;
                                                }

                                                if (
                                                    isNew &&
                                                    pollId === undefined
                                                )
                                                    return true;

                                                return (
                                                    selectedCharacter ===
                                                    sectionId
                                                );
                                            })()}
                                            handleClick={() => {
                                                if (
                                                    finalRewardVoucher === null
                                                ) {
                                                    if (votingIsActive) {
                                                        return;
                                                    }

                                                    if (
                                                        isNew &&
                                                        pollId === undefined
                                                    )
                                                        return;
                                                }

                                                toggleCharacter(
                                                    sectionId ?? "",
                                                );
                                            }}
                                            isNew={isNew}
                                            modalStyles={modalStyles}
                                            name={characterName}
                                            sectionId={sectionId}
                                            toggleVariables={toggleVariables}
                                            variables={variables}
                                            lang={lang}
                                            timer={null}
                                            handleLinkClick={handleClick}
                                            trackButtonClick={trackButtonClick}
                                            votingIsActive={votingIsActive}
                                            votingInProgress={votingInProgress}
                                            voteConfirmation={
                                                voteConfirmation ===
                                                characterName
                                                    .replace(/\s/g, "")
                                                    .replace(".", "")
                                                    .toLowerCase()
                                            }
                                            toggleVoteConfirmation={
                                                toggleVoteConfirmation
                                            }
                                            vote={handleVote}
                                            votedFor={
                                                votedForCharacter ===
                                                characterName
                                                    .replace(/\s/g, "")
                                                    .replace(".", "")
                                                    .toLowerCase()
                                            }
                                            voteResult={
                                                showResults
                                                    ? optionsWithResults.find(
                                                          (option) =>
                                                              option.title ===
                                                              characterName
                                                                  .replace(
                                                                      /\s/g,
                                                                      "",
                                                                  )
                                                                  .replace(
                                                                      ".",
                                                                      "",
                                                                  )
                                                                  .toLowerCase(),
                                                      )?.result
                                                    : undefined
                                            }
                                            hasVoted={
                                                finalRewardVoucher !== null
                                            }
                                        />
                                    );
                                },
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="error">
                    <div className="error-text">
                        <FormattedMessage id="globalText.langGoldenRippleError" />
                    </div>
                </div>
            )}
        </div>
    );
};

function Character({
    selected,
    handleClick,
    modalStyles,
    name,
    sectionId,
    variables,
    toggleVariables,
    timer,
    lang,
    isNew,
    handleLinkClick,
    trackButtonClick,
    votingIsActive,
    votingInProgress,
    voteConfirmation,
    toggleVoteConfirmation,
    vote,
    votedFor,
    voteResult,
    hasVoted,
}: {
    selected: boolean;
    handleClick: () => void;
    modalStyles: React.CSSProperties;
    name: string;
    sectionId: string;
    variables: { name: string; value: string }[];
    toggleVariables: ToggleVariablesFunction;
    timer: string | null;
    lang: string;
    isNew: boolean;
    handleLinkClick: (cta: InboxCTA) => void;
    trackButtonClick: (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => void;
    votingIsActive: boolean;
    votingInProgress: boolean;
    voteConfirmation: boolean;
    toggleVoteConfirmation: (character: string, value: boolean) => void;
    vote: (character: string) => void;
    votedFor: boolean;
    voteResult?: number;
    hasVoted: boolean;
}) {
    const { openCustomModal, closeCustomModal } = useCustomModal();
    const intl = useIntl();

    const handleModalOpen = () => {
        openCustomModal(
            <Dialog
                modalStyles={modalStyles}
                sectionId={sectionId}
                characterName={name}
                variables={variables}
                closeCustomModal={closeCustomModal}
                toggleVariables={toggleVariables}
                lang={lang}
                getLocalization={(id: string) =>
                    intl.formatMessage({ id: id.replace(". ", "") })
                }
                handleLinkClick={handleLinkClick}
                trackButtonClick={trackButtonClick}
            />,
        );

        /*
        button_id: goldenRippleOpenCharacter
        additional_information:
            characterName: "Mandy"
            characterId: "73689a02-3809-4522-aa03-ff33395f05e4"
            variables: {
                "Fang": "1",
                "Book": "0"
            }
        */
        trackButtonClick("goldenRippleOpenCharacter", undefined, {
            characterName: name,
            characterId: sectionId,
            variables: variables.reduce<Record<string, string>>((acc, curr) => {
                acc[curr.name] = curr.value;
                return acc;
            }, {}),
        });

        // Override back button behavior to close the custom modal
        const backButtonEvent = new CustomEvent("backButtonConditionSet", {
            detail: {
                backButtonBehavior: "closeCustomModal",
            },
        });
        window.dispatchEvent(backButtonEvent);

        localStorage.setItem(
            `golden-ripple-new-label-seen-${sectionId}`,
            "true",
        );
    };

    const parsedCharacterName = name
        .replace(/\s/g, "")
        .replace(".", "")
        .toLowerCase();

    return (
        <div
            className={`character ${selected ? "selected" : ""} character-${parsedCharacterName}`}
            onClick={handleClick}
        >
            <div className="image"></div>
            <div className="shadow"></div>
            {timer !== null && lang !== undefined && (
                <div className="timer-container">
                    <div className="timer-label">
                        <FormattedMessage id="globalText.langGoldenRippleUnlockingIn" />
                    </div>
                    <Timer timerString={timer} lang={String(lang)} />
                </div>
            )}
            {voteResult !== undefined && (
                <div className="result-overlay">
                    <div className="result-text">
                        {(Math.round(voteResult * 1000) / 10).toFixed(1)}%
                    </div>
                    <div className="result-shadows"></div>
                    <div className="result-border"></div>
                </div>
            )}
            {votedFor && (
                <div
                    className={`voted-overlay ${voteResult !== undefined ? "with-result" : ""}`}
                >
                    <div className="voted-icon"></div>
                    <div className="voted-text">
                        <FormattedMessage id="globalText.langVoted" />
                    </div>
                </div>
            )}
            {votingIsActive && !votingInProgress
                ? (() => {
                      if (!voteConfirmation) {
                          return (
                              <div className="voteActionWrapper">
                                  <div className="actionButton">
                                      <Button
                                          onClick={handleModalOpen}
                                          variant="gold"
                                          size="sm"
                                      >
                                          <span />
                                      </Button>
                                  </div>
                                  <div className="voteButton">
                                      <Button
                                          onClick={() => {
                                              handleClick();
                                              toggleVoteConfirmation(
                                                  parsedCharacterName,
                                                  true,
                                              );
                                          }}
                                          variant="blue"
                                          size="sm"
                                      >
                                          <span className="buttonTextWrapper">
                                              <FormattedMessage id="globalText.langVoteDefaultButtonLabel" />
                                          </span>
                                      </Button>
                                  </div>
                              </div>
                          );
                      }

                      return (
                          <div className="voteConfirmation">
                              <div className="voteConfirmationText">
                                  <FormattedMessage id="globalText.langGoldenRippleConfirmation" />
                              </div>
                              <div className="voteConfirmationButtons">
                                  <Button
                                      onClick={() => {
                                          handleClick();
                                          vote(parsedCharacterName);
                                          toggleVoteConfirmation(
                                              parsedCharacterName,
                                              false,
                                          );
                                      }}
                                      variant="green"
                                      size="sm"
                                  >
                                      <span className="buttonTextWrapper">
                                          <FormattedMessage id="globalText.langGoldenRippleConfirmationYes" />
                                      </span>
                                  </Button>
                                  <Button
                                      onClick={() => {
                                          handleClick();
                                          toggleVoteConfirmation(
                                              parsedCharacterName,
                                              false,
                                          );
                                      }}
                                      variant="red"
                                      size="sm"
                                  >
                                      <span className="buttonTextWrapper">
                                          <FormattedMessage id="globalText.langGoldenRippleConfirmationNo" />
                                      </span>
                                  </Button>
                              </div>
                          </div>
                      );
                  })()
                : !votingInProgress && (
                      <div className="actionButton">
                          <Button
                              onClick={handleModalOpen}
                              variant={isNew && !hasVoted ? "gold" : "blue"}
                              size="sm"
                          >
                              <span className="buttonTextWrapper">
                                  <FormattedMessage id="globalText.langGoldenRippleModalCTA" />
                              </span>
                          </Button>
                      </div>
                  )}
        </div>
    );
}

function Dialog({
    modalStyles,
    sectionId,
    characterName,
    variables,
    closeCustomModal,
    toggleVariables,
    lang,
    getLocalization,
    handleLinkClick,
    trackButtonClick,
}: {
    modalStyles: React.CSSProperties;
    sectionId: string;
    characterName: string;
    variables: { name: string; value: string }[];
    closeCustomModal: () => void;
    toggleVariables: ToggleVariablesFunction;
    lang: string;
    getLocalization: (id: string) => string;
    handleLinkClick: (cta: InboxCTA) => void;
    trackButtonClick: (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => void;
}) {
    const {
        messages,
        availableQuestions,
        visibleBubbleCount,
        allBubblesVisible,
        selectQuestion,
        skipAnimation,
        conversation,
        lastSelectedQuestion,
    } = useConversation({
        sectionId,
        globalVariables: variables,
        toggleVariables,
        lang,
        characterName,
    });
    const [visibleQuestionCount, setVisibleQuestionCount] = useState(0);

    useEffect(() => {
        if (!allBubblesVisible) return;
        setVisibleQuestionCount(0);

        const interval = setInterval(() => {
            setVisibleQuestionCount((prev) => {
                if (prev >= availableQuestions.length) {
                    clearInterval(interval);
                    return prev;
                }
                return prev + 1;
            });
        }, 500);

        return () => {
            clearInterval(interval);
        };
    }, [availableQuestions, allBubblesVisible]);

    const visibleQuestions = availableQuestions.slice(0, visibleQuestionCount);

    const conversationWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!conversationWrapperRef.current) return;

        conversationWrapperRef.current.scrollTop =
            conversationWrapperRef.current.scrollHeight;
    }, [visibleBubbleCount, visibleQuestionCount]);

    if (!conversation) return null;

    const shouldShowSidekick = characterName.toLowerCase() !== "pierce";

    return (
        <div className="modal-overlay" style={modalStyles}>
            <div
                className={`modal-character character-${characterName
                    .toLowerCase()
                    .replace(/\s/g, "")
                    .replace(".", "")}`}
            ></div>
            <div className="modal-chat-bg"></div>
            <div className="modal-prop"></div>
            <div
                className="modal-wrapper"
                ref={conversationWrapperRef}
                onClick={() => {
                    if (!allBubblesVisible) {
                        skipAnimation();
                    }
                }}
            >
                <div className="modal-content">
                    {messages.map((message, index) => {
                        return message.body.map((part, partIndex) => {
                            const previousBodyLength = messages
                                .slice(0, index)
                                .reduce((acc, msg) => {
                                    return acc + msg.body.length;
                                }, 0);

                            const previousBodySidekickTextCount = messages
                                .slice(0, index)
                                .reduce((acc, msg) => {
                                    return (
                                        acc +
                                        msg.body.filter(
                                            (p) => p.type === "sidekickText",
                                        ).length
                                    );
                                }, 0);

                            // Count sidekickText parts before the current partIndex in the current message body,
                            // count them essentially as double in the currentPartIndex
                            const sidekickTextsBeforeCount = message.body
                                .slice(0, partIndex)
                                .filter(
                                    (p) => p.type === "sidekickText",
                                ).length;

                            // Only add sidekick count offset if we should show the sidekick popup
                            const sidekickAdditionCount = shouldShowSidekick
                                ? previousBodySidekickTextCount > 0
                                    ? 1
                                    : sidekickTextsBeforeCount > 0
                                      ? 1
                                      : 0
                                : 0;

                            const currentPartIndex =
                                previousBodyLength +
                                partIndex +
                                sidekickAdditionCount;

                            if (
                                part.type === "sidekickText" &&
                                shouldShowSidekick &&
                                currentPartIndex ===
                                    visibleBubbleCount -
                                        (sidekickAdditionCount === 0 ? 1 : 0)
                            ) {
                                return null;
                            }

                            if (currentPartIndex >= visibleBubbleCount) {
                                return null;
                            }

                            if (!conversation) return null;

                            const isLastOfBody =
                                partIndex === message.body.length - 1 ||
                                (message.body[partIndex + 1]?.type !==
                                    "reward" &&
                                    message.body[partIndex + 1]?.type !==
                                        "link" &&
                                    message.body[partIndex + 1]?.type !==
                                        part.type) ||
                                (message.body[partIndex + 1]?.type ===
                                    "guestText" &&
                                    part.type === "guestText" &&
                                    (
                                        message.body[partIndex + 1] as
                                            | z.infer<typeof GuestTextSchema>
                                            | undefined
                                    )?.speaker !== part.speaker) ||
                                currentPartIndex + 1 === visibleBubbleCount;

                            switch (part.type) {
                                case "text":
                                    return (() => {
                                        const previousMessage =
                                            message.body[partIndex - 1];

                                        // If the previous part was sidekickText, we need to show the label again
                                        const previousIsSidekickText =
                                            partIndex > 0 &&
                                            previousMessage?.type ===
                                                "sidekickText";

                                        const previousIsSpeakerText =
                                            partIndex > 0 &&
                                            previousMessage?.type ===
                                                "guestText";

                                        return (
                                            <Fragment
                                                key={`${index}-${partIndex}`}
                                            >
                                                {(partIndex === 0 ||
                                                    previousIsSidekickText ||
                                                    previousIsSpeakerText) && (
                                                    <div
                                                        className={`from from-${message.from}`}
                                                    >
                                                        {getLocalization(
                                                            `globalText.langGoldenRippleSpeaker${
                                                                message.from ===
                                                                "character"
                                                                    ? characterName
                                                                    : "You"
                                                            }`,
                                                        )}
                                                    </div>
                                                )}
                                                <Message
                                                    from={message.from}
                                                    icon={
                                                        message.from ===
                                                        "character"
                                                            ? characterName
                                                                  .replace(
                                                                      " ",
                                                                      "",
                                                                  )
                                                                  .replace(
                                                                      ".",
                                                                      "",
                                                                  )
                                                                  .toLowerCase()
                                                            : "you"
                                                    }
                                                    showIcon={isLastOfBody}
                                                >
                                                    {part.content}
                                                </Message>
                                            </Fragment>
                                        );
                                    })();
                                case "guestText":
                                    return (() => {
                                        // We need to adjust the from labels based on surrounding text

                                        const previousIsGuestText =
                                            partIndex > 0 &&
                                            message.body[partIndex - 1]
                                                ?.type === "guestText" &&
                                            (
                                                message.body[partIndex - 1] as
                                                    | z.infer<
                                                          typeof GuestTextSchema
                                                      >
                                                    | undefined
                                            )?.speaker === part.speaker;

                                        return (
                                            <Fragment
                                                key={`${index}-${partIndex}`}
                                            >
                                                {!previousIsGuestText && (
                                                    <div className="from from-guest">
                                                        {getLocalization(
                                                            `globalText.langGoldenRippleSpeaker${
                                                                part.speaker
                                                            }`,
                                                        )}
                                                    </div>
                                                )}
                                                <Message
                                                    from="guest"
                                                    icon={part.speaker
                                                        .replace(/\s/g, "")
                                                        .replace(".", "")
                                                        .toLowerCase()}
                                                    showIcon={isLastOfBody}
                                                >
                                                    {part.content}
                                                </Message>
                                            </Fragment>
                                        );
                                    })();
                                case "sidekickText":
                                    return (() => {
                                        // We need to adjust the from labels based on surrounding text

                                        const previousIsSidekickText =
                                            partIndex > 0 &&
                                            message.body[partIndex - 1]
                                                ?.type === "sidekickText";

                                        return (
                                            <Fragment
                                                key={`${index}-${partIndex}`}
                                            >
                                                {!previousIsSidekickText && (
                                                    <div className="from from-sidekick">
                                                        {getLocalization(
                                                            "globalText.langGoldenRippleSpeakerPierce",
                                                        )}
                                                    </div>
                                                )}
                                                <Message
                                                    from="sidekick"
                                                    icon="sidekick"
                                                    showIcon={isLastOfBody}
                                                >
                                                    {part.content}
                                                </Message>
                                            </Fragment>
                                        );
                                    })();
                                case "reward":
                                    return (() => {
                                        const reward =
                                            conversation.rewards?.[
                                                part.rewardId
                                            ];

                                        const status =
                                            reward?.voucher.status ||
                                            "VOUCHER_NOT_FOUND";

                                        const previousMessage =
                                            message.body[partIndex - 1];

                                        const previousMessageCharacter =
                                            (() => {
                                                switch (previousMessage?.type) {
                                                    case "guestText":
                                                        return (
                                                            previousMessage as z.infer<
                                                                typeof GuestTextSchema
                                                            >
                                                        ).speaker;
                                                    case "sidekickText":
                                                        return "Pierce";
                                                    case "text":
                                                    default:
                                                        return characterName;
                                                }
                                            })();

                                        return (
                                            <Fragment
                                                key={`${index}-${partIndex}`}
                                            >
                                                {partIndex === 0 && (
                                                    <div
                                                        className={`from from-${message.from}`}
                                                    >
                                                        {getLocalization(
                                                            `globalText.langGoldenRippleSpeaker${
                                                                message.from ===
                                                                "character"
                                                                    ? characterName
                                                                    : "You"
                                                            }`,
                                                        )}
                                                    </div>
                                                )}
                                                <Reward
                                                    handleClick={() => {
                                                        if (reward) {
                                                            bridge().then(
                                                                async (b) => {
                                                                    if (
                                                                        reward
                                                                            .voucher
                                                                            .token ===
                                                                        undefined
                                                                    ) {
                                                                        return;
                                                                    }

                                                                    // Delete all localStorage items that start with golden-ripple-conversations- to
                                                                    // force refetching conversations (and the reward status) next time
                                                                    const localStorageKeys =
                                                                        Object.keys(
                                                                            localStorage,
                                                                        );

                                                                    localStorageKeys.forEach(
                                                                        (
                                                                            key,
                                                                        ) => {
                                                                            if (
                                                                                key.startsWith(
                                                                                    "golden-ripple-conversations-",
                                                                                )
                                                                            ) {
                                                                                localStorage.removeItem(
                                                                                    key,
                                                                                );
                                                                            }
                                                                        },
                                                                    );

                                                                    if (
                                                                        reward
                                                                            .voucher
                                                                            .status ===
                                                                        "VOUCHER_NOT_REDEEMED"
                                                                    ) {
                                                                        /*
                                                                        button_id: goldenRippleClaimReward
                                                                        additional_information:
                                                                            rewardId: "UUID of the reward, *not* Spell Factory ID"
                                                                            rewardName: "1 Chaos Drop"
                                                                        */
                                                                        trackButtonClick(
                                                                            "goldenRippleClaimReward",
                                                                            undefined,
                                                                            {
                                                                                rewardId:
                                                                                    part.rewardId,
                                                                                rewardName:
                                                                                    reward.name,
                                                                            },
                                                                        );
                                                                    }

                                                                    b.claimVoucherQuiet(
                                                                        reward
                                                                            .voucher
                                                                            .token,
                                                                    );
                                                                    b.close();
                                                                },
                                                            );
                                                        } else {
                                                            console.error(
                                                                "Reward not found:",
                                                                part.rewardId,
                                                            );
                                                        }
                                                    }}
                                                    icon={previousMessageCharacter
                                                        .toLowerCase()
                                                        .replace(/\s/g, "")
                                                        .replace(".", "")}
                                                    showIcon={isLastOfBody}
                                                    reward={part.name
                                                        .toLowerCase()
                                                        .replace(/\s/g, "")}
                                                    status={status}
                                                >
                                                    {getLocalization(
                                                        status ===
                                                            "VOUCHER_ALREADY_REDEEMED"
                                                            ? "globalText.langGoldenRippleClaimed"
                                                            : "globalText.langGoldenRippleClaim",
                                                    )}
                                                    <div className="icon"></div>
                                                    {part.name.split(" ")[0]}
                                                </Reward>
                                            </Fragment>
                                        );
                                    })();
                                case "link":
                                    return (
                                        <Fragment key={`${index}-${partIndex}`}>
                                            {partIndex === 0 && (
                                                <div
                                                    className={`from from-${message.from}`}
                                                >
                                                    {getLocalization(
                                                        `globalText.langGoldenRippleSpeaker${
                                                            message.from ===
                                                            "character"
                                                                ? characterName
                                                                : "You"
                                                        }`,
                                                    )}
                                                </div>
                                            )}
                                            <Link
                                                handleClick={() => {
                                                    handleLinkClick({
                                                        id: part.url,
                                                        label: part.label,
                                                        type: "url",
                                                        url: part.url,
                                                        variant: "red",
                                                        functionName: "",
                                                        functionArgs: [],
                                                    });
                                                }}
                                                icon={characterName
                                                    .toLowerCase()
                                                    .replace(/\s/g, "")
                                                    .replace(".", "")}
                                                showIcon={isLastOfBody}
                                            >
                                                {getLocalization(
                                                    "globalText.langGoldenRippleWatch",
                                                )}
                                            </Link>
                                        </Fragment>
                                    );
                                default:
                                    return null;
                            }
                        });
                    })}
                    {allBubblesVisible && (
                        <Options
                            visibleQuestions={visibleQuestions}
                            selectQuestion={selectQuestion}
                            closeCustomModal={closeCustomModal}
                            startSectionId={conversation.start.sectionId}
                            getLocalization={getLocalization}
                            trackButtonClick={trackButtonClick}
                            lastSelectedQuestion={
                                lastSelectedQuestion ?? {
                                    text: characterName,
                                    sectionId,
                                }
                            }
                        />
                    )}
                </div>
            </div>
            {shouldShowSidekick &&
                messages.map((message, index) => {
                    return message.body.map((part, partIndex) => {
                        const previousBodyLength = messages
                            .slice(0, index)
                            .reduce((acc, msg) => {
                                return acc + msg.body.length;
                            }, 0);

                        // We only show a sidekick popup for the first sidekickText in the conversation

                        const currentPartIndex = previousBodyLength + partIndex;
                        if (part.type !== "sidekickText") return null;

                        const firstMessageThatIncludesSidekickText =
                            messages.findIndex(
                                (msg) =>
                                    msg.body.find(
                                        (p) => p.type === "sidekickText",
                                    ) !== undefined,
                            );

                        if (index !== firstMessageThatIncludesSidekickText)
                            return null;

                        const isFirstSidekickTextInMessage =
                            message.body.findIndex(
                                (p) => p.type === "sidekickText",
                            ) === partIndex;

                        if (!isFirstSidekickTextInMessage) return null;

                        return (
                            <Sidekick
                                key={`sidekick-${index}-${partIndex}`}
                                visible={
                                    currentPartIndex === visibleBubbleCount - 1
                                }
                                getLocalization={getLocalization}
                            >
                                {part.content}
                            </Sidekick>
                        );
                    });
                })}
        </div>
    );
}

function Info({ locked }: { locked: boolean }) {
    const [open, setOpen] = useState(false);

    return (
        <div
            className="reward-wrapper"
            onClick={() => setOpen((prev) => !prev)}
        >
            <div className="box"></div>
            {locked && <div className="lock"></div>}
            <div className="info">
                <div className={`speech-bubble ${open ? "open" : ""}`}>
                    <FormattedMessage id="globalText.langGoldenRippleInstruction" />
                    <svg
                        id="Layer_1"
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        viewBox="0 0 22.8 19.9"
                    >
                        <g id="Layer_1-2">
                            <path d="M22.9,19.9L13,2.5c-.9-1.5-2.5-2.5-4.3-2.5S4.4,1.6,3.9,3.8L0,19.9h22.9Z" />
                            <path
                                style={{ fill: "#fff" }}
                                d="M19.4,19.9L10.7,3.5c-1-1.8-3.7-1.5-4.2.5l-3.8,15.8h16.7Z"
                            />
                        </g>
                    </svg>
                </div>
            </div>
        </div>
    );
}

function Sidekick({
    visible,
    children,
    getLocalization,
}: {
    visible: boolean;
    children: string;
    getLocalization: (key: string) => string;
}) {
    const [hidden, setHidden] = useState(true);

    useEffect(() => {
        if (visible) {
            setHidden(false);
        } else {
            const timeout = setTimeout(() => {
                setHidden(true);
            }, 1000);

            return () => clearTimeout(timeout);
        }
    }, [visible]);

    if (hidden) return null;

    const content = parseItalicText(children);

    return (
        <div className={`sidekick ${visible ? "visible" : ""}`}>
            <div className="speech-bubble">
                <span>{content}</span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 19.88 25.41"
                >
                    <g id="Layer_1-2" data-name="Layer 1">
                        <path
                            style={{
                                opacity: 0.25,
                            }}
                            d="M0,25.41l17.39-9.89c1.54-.87,2.49-2.51,2.49-4.28v-2.55L0,22.85v2.55Z"
                        />
                        <path d="M0,22.85l17.39-9.89c1.54-.87,2.49-2.51,2.49-4.28,0-2.27-1.56-4.25-3.77-4.79L0,0v22.85Z" />
                        <path
                            style={{
                                fill: "#fff",
                            }}
                            d="M0,19.42l16.38-8.7c1.83-.97,1.48-3.69-.53-4.18L0,2.75v16.67Z"
                        />
                    </g>
                </svg>
            </div>
            <Button onClick={() => {}} variant="twitch">
                <span className="buttonTextWrapper">
                    {getLocalization("globalText.langGoldenRippleContinue")}
                </span>
            </Button>
            <div className="character"></div>
            <div className="sidekick-shadow"></div>
        </div>
    );
}

function GesturingHand({
    shouldHide,
    lower,
}: {
    shouldHide: boolean;
    lower: boolean;
}) {
    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
        const showTimer = setTimeout(() => {
            if (shouldHide) return;
            setVisible(true);
        }, 2500);

        const hideTimer = setTimeout(() => {
            if (!shouldHide) return;
            setVisible(false);
        }, 300);

        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, [shouldHide]);

    return (
        <div
            className={`handWrapper ${lower ? "lower" : ""} ${visible ? "visible" : ""}`}
        >
            <div className="hand"></div>
        </div>
    );
}

function useConversationData(lang: string) {
    if (lang === undefined || lang === null || lang === "") {
        lang = "en";
    }

    // TODO: Investigate why this didn't work for Netherlands, gameParams maybe doesn't load correctly there?
    // The issue is that lang is empty, but if we default to "en", it still doesn't work

    const [conversationData, setConversationData] = useState<
        ConversationData["conversations"] | null
    >(null);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    const { gameParams } = useGameParamsContext();

    useEffect(() => {
        (async () => {
            const jwt = !isEmptyGameParams(gameParams)
                ? gameParams.jwt
                : undefined;

            if (!jwt) {
                console.error("No JWT available");
                setError(true);
                setLoading(false);
                return;
            }

            const jwtExpiration = getExpirationDate(jwt);

            if (jwtExpiration && jwtExpiration < new Date()) {
                console.error("JWT is expired");
                await bridge().then((b) => b.refreshJwt());
                return;
            }

            const avatarId = getAvatarId(jwt ?? "") ?? "";

            const cache = localStorage.getItem(
                `golden-ripple-conversations-${avatarId}-${lang}`,
            );

            // Use the cache if expiry date hasn't been reached yet and no publishAt dates have passed
            // This is to ensure we get new conversations that may have become active since last fetch
            if (cache) {
                try {
                    const parsedCacheData = JSON.parse(cache);

                    const validationResult = z
                        .object({
                            conversations: z.array(ConversationSchema),
                            expiresAt: z.string(),
                        })
                        .safeParse(parsedCacheData);

                    if (validationResult.success) {
                        const parsedCache = validationResult.data;

                        const expiresAt = new Date(parsedCache.expiresAt);
                        const now = new Date();

                        if (now < expiresAt) {
                            console.log("Cached data hasn't expired");

                            let shouldUpdate = false;

                            for (const cachedConversation of parsedCache.conversations) {
                                const publishAtDate = new Date(
                                    cachedConversation.publishAt,
                                );
                                if (
                                    cachedConversation.conversation === null &&
                                    now >= publishAtDate
                                ) {
                                    console.log(
                                        "Cache should update, because publishAtDate has passed now and conversation was null",
                                        publishAtDate,
                                    );
                                    shouldUpdate = true;
                                    break;
                                }
                            }

                            if (!shouldUpdate) {
                                console.log(
                                    "Using cached conversation data, expires at:",
                                    expiresAt.toLocaleString(),
                                );
                                setConversationData(parsedCache.conversations);
                                setLoading(false);
                                setError(false);
                                return;
                            }
                        } else {
                            console.log("Cached conversation data is expired");
                        }
                    } else {
                        console.error(
                            "Failed to validate cached conversation data:",
                            validationResult.error,
                        );
                    }
                } catch (error) {
                    console.error(
                        "Failed to parse cached conversation data",
                        error,
                    );
                }
            }

            const res = await fetch(
                `${window.GOLDEN_RIPPLE_API_BASE}/get-conversation?lang=${lang}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${jwt}`,
                    },
                },
            );
            if (res.status !== 200) {
                setError(true);
                setLoading(false);
                throw new Error(
                    `Failed to fetch conversation data: ${res.status}`,
                );
            }
            const data = await res.json();

            const validationResult = ConversationDataSchema.safeParse(data);

            if (!validationResult.success) {
                setError(true);
                setLoading(false);
                console.error(
                    "Failed to validate conversation data:",
                    validationResult.error,
                );
                return;
            }

            const conversations = validationResult.data.conversations;

            setError(false);
            setLoading(false);
            setConversationData(conversations);

            const expiresAt = (() => {
                const now = new Date();
                const next8amUtc = new Date(
                    Date.UTC(
                        now.getUTCFullYear(),
                        now.getUTCMonth(),
                        now.getUTCDate(),
                        8,
                        0,
                        0,
                    ),
                );
                if (now > next8amUtc) {
                    next8amUtc.setUTCDate(next8amUtc.getUTCDate() + 1);
                }
                return next8amUtc;
            })();

            localStorage.setItem(
                `golden-ripple-conversations-${avatarId}-${lang}`,
                JSON.stringify({ expiresAt, conversations }),
            );
        })().catch((error) => {
            console.error("Error fetching conversation data:", error);
            setError(true);
            setLoading(false);
        });
    }, [gameParams]);

    return { conversations: conversationData, error, loading };
}

function useConversation({
    sectionId,
    globalVariables,
    toggleVariables,
    lang,
    characterName,
}: {
    sectionId: string;
    globalVariables: { name: string; value: string }[];
    toggleVariables: ToggleVariablesFunction;
    lang: string;
    characterName: string;
}) {
    const { conversations, loading } = useConversationData(lang);
    const [messages, setMessages] = useState<Message[]>([]);
    const [lastSelectedQuestion, setLastSelectedQuestion] = useState<{
        text: string;
        sectionId: string;
    } | null>(null);

    const [availableQuestions, setAvailableQuestions] = useState<
        Array<{
            text: string;
            sectionId: string;
        }>
    >([]);

    const [visibleBubbleCount, setVisibleBubbleCount] = useState(0);
    const bubbleIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const shouldShowSidekick = characterName.toLowerCase() !== "pierce";
    const bubbleCount = getAllBubblesCount(messages, shouldShowSidekick);
    const allBubblesVisible = bubbleCount === visibleBubbleCount;

    const skipAnimation = () => {
        handleVisibleBubbleIncrement(1, messages);
    };

    const handleVisibleBubbleIncrement = (
        count: number,
        messages: Message[],
    ) => {
        const bubbleCount = getAllBubblesCount(messages, shouldShowSidekick);

        // Always clear any existing interval
        if (bubbleIntervalRef.current) {
            clearInterval(bubbleIntervalRef.current);
            bubbleIntervalRef.current = null;
        }

        setVisibleBubbleCount((prev) => {
            if (prev >= bubbleCount) {
                return prev;
            }

            const nextVisibleCount = prev + count;

            // Only pause for sidekick popup if we should show it (i.e., not Pierce's dialog)
            if (shouldShowSidekick) {
                let bubbleCountBeforeFirstSidekick = 0;
                let messagesHaveSidekick = false;
                for (const message of messages) {
                    for (const part of message.body) {
                        if (part.type === "sidekickText") {
                            messagesHaveSidekick = true;
                            break;
                        } else {
                            bubbleCountBeforeFirstSidekick++;
                        }
                    }

                    if (messagesHaveSidekick) break;
                }

                if (
                    messagesHaveSidekick &&
                    bubbleCountBeforeFirstSidekick + 1 === nextVisibleCount
                ) {
                    console.log("Pausing before sidekick popup");
                    return nextVisibleCount;
                }
            }

            // Create new interval for next increment
            bubbleIntervalRef.current = setInterval(() => {
                handleVisibleBubbleIncrement(1, messages);
            }, 1000);

            return nextVisibleCount;
        });
    };

    const handleNewMessages = (newMessages: Message[]) => {
        setMessages((prev) => {
            const messages: Message[] = [...prev, ...newMessages];

            const bubbleCount = getAllBubblesCount(
                messages,
                shouldShowSidekick,
            );

            if (bubbleCount > 0) {
                handleVisibleBubbleIncrement(1, messages);
            }

            return messages;
        });
    };

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (bubbleIntervalRef.current) {
                clearInterval(bubbleIntervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const conversation = conversations?.find((c) => {
            if (!c.conversation) return false;

            const foundSection = c.conversation.sections.find(
                (section) => section.id === sectionId,
            );

            return !!foundSection;
        })?.conversation;

        if (!conversation) return;

        const startSection = conversation.sections.find((section) => {
            if (section.id !== sectionId) return false;
            if (!section.condition) return true;

            const sectionConditionName = section.condition.variable;
            const sectionConditionValue = section.condition.value;

            const globalVariable = globalVariables.find(
                (variable) => variable.name === sectionConditionName,
            );

            return globalVariable?.value === sectionConditionValue;
        });

        if (startSection) {
            handleNewMessages([
                {
                    from: "character",
                    body: startSection.body,
                },
            ]);

            setAvailableQuestions(
                startSection.questions.filter((question) => {
                    return question.sectionId !== undefined;
                }) as Array<{
                    text: string;
                    sectionId: string;
                }>,
            );
            toggleVariables(startSection.variables, {
                sectionId: startSection.id,
                text: "",
            });
        }
    }, [sectionId, conversations]);

    const conversation = conversations?.find((c) => {
        if (!c.conversation) return false;

        const foundSection = c.conversation.sections.find(
            (section) => section.id === sectionId,
        );

        return !!foundSection;
    })?.conversation;

    const selectQuestion = (question: { text: string; sectionId: string }) => {
        if (!conversation) return;

        const nextSection = conversation.sections.find((section) => {
            if (section.id !== question.sectionId) return false;
            if (!section.condition) return true;

            const sectionConditionName = section.condition.variable;
            const sectionConditionValue = section.condition.value;

            const globalVariable = globalVariables.find(
                (variable) => variable.name === sectionConditionName,
            );

            return globalVariable?.value === sectionConditionValue;
        });

        if (nextSection) {
            handleNewMessages([
                {
                    from: "you",
                    body: [{ type: "text", content: question.text }],
                },
                {
                    from: "character",
                    body: nextSection.body,
                },
            ]);

            setAvailableQuestions(
                nextSection.questions.filter((question) => {
                    return question.sectionId !== undefined;
                }) as Array<{
                    text: string;
                    sectionId: string;
                }>,
            );
            setLastSelectedQuestion(question);
            toggleVariables(nextSection.variables, question);
        }
    };

    return {
        conversation,
        messages,
        availableQuestions,
        visibleBubbleCount,
        allBubblesVisible,
        lastSelectedQuestion,
        loading,
        selectQuestion,
        skipAnimation,
    };
}

function getAllBubblesCount(
    messages: Message[],
    shouldShowSidekick: boolean = true,
) {
    const firstSidekickTextExists =
        messages.find((message) =>
            message.body.find((part) => part.type === "sidekickText"),
        ) !== undefined;

    const totalBubbles = messages.reduce((acc, message) => {
        // The first sidekickText should have two visible count steps
        return acc + message.body.length;
    }, 0);

    // Only add extra count for sidekick popup if we should show it
    return (
        totalBubbles + (firstSidekickTextExists && shouldShowSidekick ? 1 : 0)
    );
}

function parseItalicText(text: string): React.ReactNode[] {
    return text.split(/(\/\/.*?\/\/|\*.*?\*)/g).map((part, index) => {
        // Check if it's wrapped in // or *
        if (
            (part.startsWith("//") && part.endsWith("//")) ||
            (part.startsWith("*") && part.endsWith("*"))
        ) {
            // Strip all nested // and * markers to create a single italic element
            let italicText = part;
            while (
                (italicText.startsWith("//") &&
                    italicText.endsWith("//") &&
                    italicText.length > 4) ||
                (italicText.startsWith("*") &&
                    italicText.endsWith("*") &&
                    italicText.length > 2)
            ) {
                if (italicText.startsWith("//") && italicText.endsWith("//")) {
                    italicText = italicText.slice(2, -2);
                } else if (
                    italicText.startsWith("*") &&
                    italicText.endsWith("*")
                ) {
                    italicText = italicText.slice(1, -1);
                }
            }
            return (
                <em key={index} className="italic">
                    {italicText}
                </em>
            );
        } else if (part.trim() !== "") {
            return <span key={index}>{part}</span>;
        } else {
            return null;
        }
    });
}

function Message({
    from,
    children,
    icon,
    showIcon,
}: {
    from: "character" | "sidekick" | "you" | "guest";
    children: string;
    icon: string;
    showIcon: boolean;
}) {
    const content = parseItalicText(children);

    return (
        <div
            className={`message from-${from} ${icon ? `character-${icon}` : ""} ${showIcon ? "last" : ""}`}
        >
            <div className="bubble">{content}</div>
        </div>
    );
}

function Link({
    handleClick,
    children,
    icon,
    showIcon,
}: {
    handleClick: () => void;
    children: React.ReactNode;
    icon: string;
    showIcon: boolean;
}) {
    return (
        <div
            className={`message link ${icon ? `character-${icon}` : ""} ${showIcon ? "last" : ""}`}
            onClick={handleClick}
        >
            <Pressable>
                <div className="bubble">
                    <div className="shadows"></div>
                    {children}
                    <div className="icon"></div>
                </div>
            </Pressable>
        </div>
    );
}

function Reward({
    handleClick,
    children,
    icon,
    showIcon,
    reward,
    status,
}: {
    handleClick: () => void;
    children: React.ReactNode;
    icon: string;
    showIcon: boolean;
    reward: string;
    status: VoucherStatus;
}) {
    const statusClass = (() => {
        switch (status) {
            case "VOUCHER_ALREADY_REDEEMED":
                return "claimed";
            case "VOUCHER_NOT_REDEEMED":
                return "claimable";
            case "VOUCHER_EXPIRED":
            case "VOUCHER_NOT_FOUND":
            default:
                return "notClaimable";
        }
    })();

    const message = (
        <div className="bubble" onClick={handleClick}>
            <div className="shadows"></div>
            {children}
        </div>
    );

    return (
        <div
            className={`message reward reward-${reward} ${statusClass} ${icon ? `character-${icon}` : ""} ${showIcon ? "last" : ""}`}
        >
            {status === "VOUCHER_NOT_REDEEMED" ? (
                <Pressable>{message}</Pressable>
            ) : (
                <>
                    {status === "VOUCHER_ALREADY_REDEEMED" && (
                        <div className="icon claimed"></div>
                    )}
                    {message}
                </>
            )}
        </div>
    );
}

function Options({
    visibleQuestions,
    selectQuestion,
    closeCustomModal,
    startSectionId,
    getLocalization,
    trackButtonClick,
    lastSelectedQuestion,
}: {
    visibleQuestions: Array<{
        text: string;
        sectionId: string;
    }>;
    selectQuestion: (question: { text: string; sectionId: string }) => void;
    closeCustomModal: () => void;
    startSectionId: string;
    getLocalization: (id: string) => string;
    trackButtonClick: (
        eventId: string,
        label?: string,
        properties?: Record<string, unknown>,
    ) => void;
    lastSelectedQuestion: {
        text: string;
        sectionId: string;
    } | null;
}) {
    const trackSelectOption = (question: {
        text: string;
        sectionId: string;
    }) => {
        /*
        button_id: goldenRippleSelectOption
        additional_information:
            selectedOptionLabel: "Sure"
            selectedOptionDestinationSectionId: "73689a02-3809-4522-aa03-ff33395f05e4"
            previousSectionId: "a4064184-d0e0-4b64-8032-e4efa6a27bb5"
            previousSectionLabel: "900,000"
            availableOptions: [
                {
                    label: "Sure",
                    destinationSectionId: "73689a02-3809-4522-aa03-ff33395f05e4",
                },
                {
                    label: "No way",
                    destinationSectionId: "333f5ca3-bccf-411a-b7f7-522697437a4a",
                },
            ]
        */
        trackButtonClick("goldenRippleSelectOption", undefined, {
            selectedOptionLabel: question.text,
            selectedOptionDestinationSectionId: question.sectionId,
            previousSectionId: lastSelectedQuestion?.sectionId,
            previousSectionLabel: lastSelectedQuestion?.text,
            availableOptions: visibleQuestions.map((q) => ({
                label: q.text,
                destinationSectionId: q.sectionId,
            })),
        });
    };

    return (
        <>
            {visibleQuestions.length > 0 && (
                <div className="from from-you">
                    {getLocalization("globalText.langGoldenRippleSpeakerYou")}
                </div>
            )}
            <div className="message options-message">
                <div className="options">
                    {visibleQuestions.map((question, index) => {
                        if (question.sectionId === startSectionId) {
                            return (
                                <Pressable key={index}>
                                    <div
                                        className="option"
                                        onClick={() => {
                                            closeCustomModal();
                                            trackSelectOption(question);
                                        }}
                                    >
                                        {getLocalization(
                                            "globalText.langGoldenRippleBackToStart",
                                        )}
                                    </div>
                                </Pressable>
                            );
                        }

                        return (
                            <Pressable key={index}>
                                <div
                                    className="option"
                                    onClick={() => {
                                        selectQuestion(question);
                                        trackSelectOption(question);
                                    }}
                                >
                                    {parseItalicText(question.text)}
                                </div>
                            </Pressable>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

function useImageLoader(urls: string[]) {
    const [imageLoadCount, setImageLoadCount] = useState(0);
    const [imageUrls, setImageUrls] = useState<string[]>();

    useEffect(() => {
        setImageLoadCount(0);
        setImageUrls(urls);
    }, [urls]);

    useEffect(() => {
        if (!imageUrls) return;
        imageUrls.forEach((url) => {
            const image = new Image();
            image.src = url;
            image.onload = () => {
                setImageLoadCount((count) => count + 1);
            };
            image.onerror = () => {
                setImageLoadCount((count) => count + 1);
            };
        });
    }, [imageUrls]);

    const loaded = imageLoadCount === imageUrls?.length;

    return loaded;
}
